import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchAllPages, purchasingAPI } from '../../lib/api';
import type { ContractPrice } from '../../lib/api/types/contract-types';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { ListPager, ListSearch, useListControls } from '../../components/common/listControls';
import { SortableTh } from '../../components/common/SortableTh';
import { UploadTableEmpty, UploadTableSkeleton } from '../barratt/upload-history/UploadTableStates';
import { formatDay } from '../barratt/uploadCycle';
import { AlertCircle, Download, RefreshCw } from 'lucide-react';
import * as XLSX from 'xlsx';

type Filter = 'live' | 'ending' | 'expired' | 'all';

const GOOD = 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-900';
const DUE = 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-950/30 dark:text-yellow-300 dark:border-yellow-900';
const LATE = 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900';
const ENDING_DAYS = 90;

function daysLeft(c: ContractPrice): number | null {
  if (!c.valid_to) return null;
  return Math.round((new Date(c.valid_to).getTime() - Date.now()) / 86_400_000);
}

/**
 * Every relationship contract in scope — the agreement itself (term, notice,
 * renewal, owner, review cadence), as opposed to the pricebooks. Contracts are
 * created and edited on the supplier's profile; this is the cross-supplier view.
 */
export default function RelationshipContractsTable({ readOnly }: { readOnly: boolean }) {
  const { getViewQueryParams, viewKey } = useView();
  const [rows, setRows] = useState<ContractPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('live');
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const scope: Record<string, string | number | boolean> = {};
    Object.entries(getViewQueryParams()).forEach(([k, v]) => { if (v !== undefined) scope[k] = v; });
    setLoading(true);
    setError(null);
    fetchAllPages<ContractPrice>((page) => purchasingAPI.getContractPrices({ ...scope, realm: 'relationship', page, page_size: 200 }))
      .then((data) => { if (!cancelled) setRows(data); })
      .catch((err: unknown) => {
        if (cancelled) return;
        console.error('Contracts load failed:', err);
        setError(getErrorMessage(err, 'Could not load contracts'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [getViewQueryParams, viewKey, tick]);

  const filtered = useMemo(() => rows.filter((c) => {
    const left = daysLeft(c);
    if (filter === 'live') return c.status === 'active';
    if (filter === 'ending') return c.status === 'active' && left !== null && left <= ENDING_DAYS;
    if (filter === 'expired') return c.status === 'expired' || (left !== null && left < 0);
    return true;
  }), [rows, filter]);

  const counts = useMemo(() => ({
    live: rows.filter((c) => c.status === 'active').length,
    ending: rows.filter((c) => c.status === 'active' && (daysLeft(c) ?? 999) <= ENDING_DAYS).length,
    expired: rows.filter((c) => c.status === 'expired' || (daysLeft(c) ?? 0) < 0).length,
    all: rows.length,
  }), [rows]);

  const list = useListControls(filtered, {
    searchText: (c) => [c.contract_number, c.supplier_name, c.contract_type_name ?? '', c.owner_name ?? ''].join(' '),
    sorters: {
      number: (c) => c.contract_number,
      supplier: (c) => c.supplier_name.toLowerCase(),
      type: (c) => c.contract_type_name ?? '',
      valid_to: (c) => c.valid_to,
      owner: (c) => (c.owner_name ?? '').toLowerCase(),
      status: (c) => c.status,
    },
    defaultSort: { key: 'valid_to', dir: 'asc' },
    tieBreak: (c) => c.contract_number,
    resetKeys: [filter],
  });

  const exportExcel = () => {
    const sheet = XLSX.utils.aoa_to_sheet([
      ['Contract', 'Supplier', 'Type', 'Valid from', 'Valid to', 'Days left', 'Owner', 'Auto-renew', 'Notice (months)', 'Reminder (days)', 'Review every (months)', 'Status'],
      ...list.orderedRows.map((c) => [
        c.contract_number, c.supplier_name, c.contract_type_name ?? '', c.valid_from, c.valid_to, daysLeft(c) ?? '',
        c.owner_name ?? '', c.auto_renew ? 'Yes' : 'No', c.notice_period_months ?? '', c.reminder_days ?? '', c.br_frequency_months ?? '', c.status,
      ]),
    ]);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Contracts');
    XLSX.writeFile(book, `contracts-${filter}.xlsx`);
  };

  const columns = 8;
  const emptyMessage = rows.length === 0
    ? (readOnly ? 'No contracts have been recorded with your company.' : 'No relationship contracts yet — add one from a supplier\'s profile, Contracts tab.')
    : list.search ? `No contracts match "${list.search}".` : 'No contracts in this view.';

  if (error) {
    return (
      <Card><CardContent className="flex flex-col items-center justify-center gap-3 py-12 text-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-sm font-medium">Could not load contracts</p>
        <p className="max-w-md text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={() => setTick((t) => t + 1)}><RefreshCw className="mr-2 h-4 w-4" />Retry</Button>
      </CardContent></Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as Filter)}>
          <TabsList data-testid="contracts-filter">
            <TabsTrigger value="live">Live ({counts.live})</TabsTrigger>
            <TabsTrigger value="ending">Ending within {ENDING_DAYS} days ({counts.ending})</TabsTrigger>
            <TabsTrigger value="expired">Expired ({counts.expired})</TabsTrigger>
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          <div className="md:w-72">
            <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search contract, supplier, type, owner…" testId="contracts-search" />
          </div>
          <Button variant="outline" size="sm" onClick={exportExcel} disabled={list.orderedRows.length === 0} data-testid="contracts-export">
            <Download className="mr-2 h-4 w-4" />Export
          </Button>
        </div>
      </div>

      <Card><CardContent className="p-0">
        <ListPager list={list} position="top" noun="contracts" testIdPrefix="contracts" />
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <SortableTh field="number" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Contract</SortableTh>
              <SortableTh field="supplier" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Supplier</SortableTh>
              <SortableTh field="type" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Type</SortableTh>
              <SortableTh field="valid_to" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Ends</SortableTh>
              <TableHead>Renewal</TableHead>
              <SortableTh field="owner" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Owner</SortableTh>
              <TableHead>Review</TableHead>
              <SortableTh field="status" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Status</SortableTh>
            </TableRow></TableHeader>
            {loading ? <UploadTableSkeleton columns={columns} />
              : list.pageRows.length === 0 ? <UploadTableEmpty columns={columns} message={emptyMessage} />
              : (
                <TableBody>
                  {list.pageRows.map((c) => {
                    const left = daysLeft(c);
                    const ending = c.status === 'active' && left !== null && left <= ENDING_DAYS;
                    return (
                      <TableRow key={c.id} className={ending && left !== null && left < 0 ? 'border-l-4 border-l-red-500' : ''} data-testid={`contract-${c.id}`}>
                        <TableCell className="font-medium">
                          {readOnly ? c.contract_number
                            : <Link to={`/suppliers/${c.supplier}?tab=contracts`} className="text-primary hover:underline">{c.contract_number}</Link>}
                          {c.supplier_contract_reference && <div className="text-xs text-muted-foreground">Their ref {c.supplier_contract_reference}</div>}
                        </TableCell>
                        <TableCell>{c.supplier_name}</TableCell>
                        <TableCell>{c.contract_type_name || '—'}</TableCell>
                        <TableCell>
                          <div>{formatDay(c.valid_to)}</div>
                          {left !== null && c.status === 'active' && (
                            <div className={`text-xs ${left < 0 ? 'text-red-600 dark:text-red-400' : ending ? 'text-yellow-700 dark:text-yellow-400' : 'text-muted-foreground'}`}>
                              {left < 0 ? `${-left} days ago` : `${left} days left`}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {c.auto_renew ? <span>Auto-renews{c.renewal_period_years ? ` · ${c.renewal_period_years}y` : ''}</span> : <span className="text-muted-foreground">Fixed term</span>}
                          {c.notice_period_months ? <div className="text-xs text-muted-foreground">{c.notice_period_months}-month notice</div> : null}
                        </TableCell>
                        <TableCell>{c.owner_name || <span className="text-muted-foreground">Unassigned</span>}</TableCell>
                        <TableCell className="text-sm">{c.br_frequency_months ? `Every ${c.br_frequency_months} months` : <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={c.status === 'active' ? (ending ? DUE : GOOD) : c.status === 'expired' ? LATE : 'bg-muted text-muted-foreground'}>
                            {c.status === 'active' ? (ending ? 'Ending soon' : 'Live') : c.status === 'expired' ? 'Expired' : 'Draft'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
          </Table>
        </div>
        <ListPager list={list} position="bottom" noun="contracts" testIdPrefix="contracts" />
      </CardContent></Card>
    </div>
  );
}
